// Player settings, stored locally (no account). Every storage call is guarded: private windows,
// blocked storage and Safari's 7-day eviction must never break play (GOAL.md rules).
export type Theme = "classic" | "contrast" | "dark"
/** "pictures": Kennard's CC0 deck with Goodall courts; "simple": large letters only (low-vision decks' pattern). */
export type Faces = "pictures" | "simple"

export interface Settings {
  textScale: number // 1 to 2
  cardScale: number // 1 to 1.6
  theme: Theme
  motion: boolean
  sound: boolean
  faces: Faces
  timer: boolean // show the count-up clock in the stats bar
}

export const DEFAULTS: Settings = { textScale: 1, cardScale: 1, theme: "classic", motion: true, sound: false, faces: "pictures", timer: true }
const KEY = "dt:settings:v1"

export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n))
}

export function parse(raw: string | null): Settings {
  if (!raw) return { ...DEFAULTS, motion: !systemReducedMotion() }
  try {
    const v = JSON.parse(raw) as Partial<Settings>
    return {
      textScale: clamp(Number(v.textScale) || 1, 1, 2),
      cardScale: clamp(Number(v.cardScale) || 1, 1, 1.6),
      theme: v.theme === "contrast" || v.theme === "dark" ? v.theme : "classic",
      // Not chosen yet: follow the device's reduced-motion setting (the games pace their moves from this).
      motion: v.motion === undefined ? !systemReducedMotion() : v.motion !== false,
      sound: v.sound === true,
      faces: v.faces === "simple" ? "simple" : "pictures",
      timer: v.timer !== false,
    }
  } catch {
    return { ...DEFAULTS }
  }
}

function systemReducedMotion(): boolean {
  try { return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches } catch { return false }
}

export function load(storage: Pick<Storage, "getItem"> | null = safeStorage()): Settings {
  try {
    return parse(storage ? storage.getItem(KEY) : null)
  } catch {
    return { ...DEFAULTS }
  }
}

export function save(s: Settings, storage: Pick<Storage, "setItem"> | null = safeStorage()): void {
  try {
    storage?.setItem(KEY, JSON.stringify(s))
  } catch {
    /* storage unavailable: settings last for this page only */
  }
}

export function apply(s: Settings, root: HTMLElement = document.documentElement): void {
  root.style.setProperty("--text-scale", String(s.textScale))
  root.style.setProperty("--card-scale", String(s.cardScale))
  // Largest text sizes: the stats bar wraps to several lines, so it stops being sticky (application.tailwind.css).
  if (s.textScale >= 1.5) root.dataset.bigText = "on"
  else delete root.dataset.bigText
  if (s.theme === "classic") delete root.dataset.theme
  else root.dataset.theme = s.theme
  root.dataset.motion = s.motion ? "on" : "off"
  root.dataset.faces = s.faces
  root.dataset.timer = s.timer ? "on" : "off"
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}
